import { c as _c } from "react/compiler-runtime";
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { ApiKeyScope, ApiKeyScopeHolder, full_access_scopes, isFullAccess, project_surface_families, read_only_families, readOnlyScopes, scope_families, ScopeFamily } from '../../../../../../domain/api-key-scopes';
import { APITeam, TeamType } from '../../../../../../typings/Team.interface';
import { StyledCard, StyledCardSection } from '../base/Card';
import Icon from '../base/Icon';
import Text from '../base/Text';
import TextSwitch from '../base/TextSwitch';
import RadioCardsInput from '../Form/Fields/RadioCardsInput';
import { Div } from '../helpers/StyledUtils';
import { ProjectAccessFormValues } from './ProjectAccessFields';
export type ApiKeyTier = 'organization' | 'project';
type ScopeLevel = 'none' | 'read' | 'write';
export interface ApiKeyScopeFormValues {
  scope_preset: string;
  custom_scopes: Record<string, ScopeLevel>;
}
const family_labels: Partial<Record<ScopeFamily, string>> = {
  organizations: 'Organization',
  projects: 'Projects',
  'api-keys': 'Project API keys',
  'gateway.issue-triggers': 'Issue triggers',
  'gateway.publish': 'Publish API'
};

// Families render grouped by API surface, so labels drop the prefix.
const familyLabel = (family: ScopeFamily): string => {
  const explicit_label = family_labels[family];
  if (explicit_label) return explicit_label;
  const separator = family.indexOf('.');
  const resource = separator === -1 ? family : family.slice(separator + 1);
  return `${resource.charAt(0).toUpperCase()}${resource.slice(1).replace(/-/g, ' ')}`;
};
const scope_groups: {
  label: string;
  surface?: string;
}[] = [{
  label: 'Platform'
}, {
  label: 'Event Gateway',
  surface: 'gateway'
}, {
  label: 'Outpost',
  surface: 'outpost'
}];
const familiesForTier = (tier: ApiKeyTier): ScopeFamily[] => tier === 'organization' ? [...scope_families] : project_surface_families;
const groupFamilies = (tier: ApiKeyTier, surface?: string): ScopeFamily[] => familiesForTier(tier).filter(family => surface ? family.startsWith(`${surface}.`) : !family.includes('.'));
const surfaceAppliesToProjects = (surface: string, project_types?: TeamType[]): boolean => !project_types || project_types.some(type => surface === 'outpost' ? type === 'outpost' : type !== 'outpost');
const scopeAppliesToProjects = (scope: string, project_types?: TeamType[]): boolean => {
  const surface = scope.split('.')[0];
  return !['gateway', 'outpost'].includes(surface) || surfaceAppliesToProjects(surface, project_types);
};
export const organizationScopeProjectTypes = (values: ProjectAccessFormValues, projects: Pick<APITeam, 'id' | 'type'>[]): TeamType[] | undefined =>
// All-project keys can also reach projects created in the future.
values.project_access === 'all' ? undefined : projects.filter(project => values.project_ids.includes(project.id)).map(({
  type
}) => type);

// Families capped at read level in the matrix: read-only surfaces everywhere.
const maxLevelForFamily = (family: ScopeFamily): ScopeLevel => {
  if (read_only_families.includes(family)) return 'read';
  return 'write';
};
const scope_level_options: {
  key: ScopeLevel;
  label: string;
}[] = [{
  key: 'none',
  label: 'None'
}, {
  key: 'read',
  label: 'Read'
}, {
  key: 'write',
  label: 'Write'
}];
const scope_preset_options = [{
  value: 'full',
  label: 'All scopes'
}, {
  value: 'readonly',
  label: 'Read-only'
}, {
  value: 'custom',
  label: 'Specific scopes'
}];

/**
 * Read-only preset scopes. Organization keys use the shared domain preset
 * (which deliberately omits `api-keys` — listing keys exposes key material);
 * project keys get read on every family they can hold.
 */
const readOnlyScopesForTier = (tier: ApiKeyTier, project_types?: TeamType[]): ApiKeyScope[] => (tier === 'organization' ? readOnlyScopes() : familiesForTier('project').map(family => `${family}.read` as ApiKeyScope)).filter(scope => scopeAppliesToProjects(scope, project_types));
const initialCustomScopes = (tier: ApiKeyTier): Record<string, ScopeLevel> => Object.fromEntries(familiesForTier(tier).map(family => [family, 'none']));
export const getInitialScopeValues = (tier: ApiKeyTier): ApiKeyScopeFormValues => ({
  scope_preset: tier === 'organization' ? 'custom' : 'full',
  custom_scopes: initialCustomScopes(tier)
});
export const scopesFromScopeValues = (tier: ApiKeyTier, values: ApiKeyScopeFormValues, project_types?: TeamType[]): ApiKeyScope[] => {
  switch (values.scope_preset) {
    case 'full':
      return full_access_scopes;
    case 'readonly':
      return readOnlyScopesForTier(tier, project_types);
    default:
      return familiesForTier(tier).filter(family => scopeAppliesToProjects(family, project_types)).filter(family => values.custom_scopes[family] && values.custom_scopes[family] !== 'none').map(family => `${family}.${values.custom_scopes[family]}` as ApiKeyScope);
  }
};
const levelFromScopes = (family: ScopeFamily, scopes: string[]): ScopeLevel => {
  if (scopes.includes(`${family}.*`) || scopes.includes(`${family}.write`)) return 'write';
  if (scopes.includes(`${family}.read`)) return 'read';
  return 'none';
};
const sameScopeSet = (a: string[], b: string[]): boolean => a.length === b.length && b.every(scope => a.includes(scope));

/** Reverse of scopesFromScopeValues: map a key's scope list back to form values. */
export const scopeValuesFromScopes = (tier: ApiKeyTier, scopes: string[], project_types?: TeamType[]): ApiKeyScopeFormValues => {
  if (isFullAccess(scopes)) {
    return {
      scope_preset: 'full',
      custom_scopes: initialCustomScopes(tier)
    };
  }
  return {
    scope_preset: sameScopeSet(scopes, readOnlyScopesForTier(tier)) || sameScopeSet(scopes, readOnlyScopesForTier(tier, project_types)) ? 'readonly' : 'custom',
    custom_scopes: Object.fromEntries(familiesForTier(tier).map(family => [family, levelFromScopes(family, scopes)]))
  };
};

/**
 * Derive the organization create/update form values from an existing key.
 * Selected-projects keys recombine the org-surface root scopes with the first
 * grant's scopes — the server derives identical scopes for every granted
 * project, so the first grant stands in for all of them.
 */
export const organizationScopeValuesFromApiKey = (api_key: ApiKeyScopeHolder, projects: Pick<APITeam, 'id' | 'type'>[]): ApiKeyScopeFormValues & {
  project_access: string;
  project_ids: string[];
} => {
  const grants = api_key.grants ?? {};
  const grant_ids = Object.keys(grants);
  const root_scopes = api_key.scopes ?? ['*'];
  if (grant_ids.length === 0) {
    return {
      ...scopeValuesFromScopes('organization', root_scopes),
      project_access: 'all',
      project_ids: []
    };
  }
  const combined_scopes = [...root_scopes, ...grants[grant_ids[0]].scopes];
  const project_types = organizationScopeProjectTypes({
    project_access: 'selected',
    project_ids: grant_ids
  }, projects);
  return {
    ...scopeValuesFromScopes('organization', combined_scopes, project_types),
    project_access: 'selected',
    project_ids: grant_ids
  };
};

/**
 * Build the create/update payload for an organization key. The full scope list
 * always travels at the root; restricting the key to selected projects only
 * names them in grants (`true` values) — the server derives the stored
 * per-project split. "All projects" sends empty grants so an update clears any
 * previous selection.
 */
export const buildOrganizationApiKeyScopesPayload = (values: ApiKeyScopeFormValues & {
  project_access: string;
  project_ids: string[];
}, projects: Pick<APITeam, 'id' | 'type'>[]): {
  scopes: ApiKeyScope[];
  grants: Record<string, true>;
} => ({
  scopes: scopesFromScopeValues('organization', values, organizationScopeProjectTypes(values, projects)),
  grants: values.project_access === 'all' ? {} : Object.fromEntries(values.project_ids.map(team_id => [team_id, true as const]))
});

/**
 * Summarize a key's permissions for display. Considers root scopes and every
 * per-subject grant: a key whose write access lives entirely inside grants
 * (organization keys restricted to selected projects) is not read-only.
 */
export const describeApiKeyScopes = ({
  scopes,
  grants
}: ApiKeyScopeHolder): string => {
  if (!scopes || isFullAccess(scopes)) return 'All';
  const all_scopes = [...scopes, ...Object.values(grants ?? {}).flatMap(grant => grant.scopes)];
  if (all_scopes.length === 0) return 'None';
  if (all_scopes.every(scope => scope.endsWith('.read'))) return 'Read';
  return 'Mixed';
};
function ScopeLevelSwitch(t0) {
  const $ = _c(12);
  const {
    family
  } = t0;
  const {
    values,
    setFieldValue
  } = useFormikContext();
  let t1;
  if ($[0] !== family) {
    t1 = maxLevelForFamily(family);
    $[0] = family;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const max_level = t1;
  let t2;
  if ($[2] !== max_level) {
    t2 = scope_level_options.filter(option => option.key === "none" || max_level === "write" || option.key === "read");
    $[2] = max_level;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = values.custom_scopes[family];
  let t4;
  if ($[4] !== family || $[5] !== setFieldValue || $[6] !== values.custom_scopes) {
    t4 = level => setFieldValue("custom_scopes", {
      ...values.custom_scopes,
      [family]: level
    });
    $[4] = family;
    $[5] = setFieldValue;
    $[6] = values.custom_scopes;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <TextSwitch small={true} options={t2} active={t3} onSelect={t4} />;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function ScopeGroupCard(t0) {
  const $ = _c(18);
  const {
    label,
    families
  } = t0;
  const [is_collapsed, setIsCollapsed] = useState(false);
  const is_expanded = !is_collapsed;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 3,
      y: 1
    };
    t2 = {
      align: "center",
      gap: 1
    };
    t3 = {
      width: "100%",
      border: "none",
      cursor: "pointer"
    };
    t4 = () => setIsCollapsed(_temp);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label) {
    t5 = <Text semi={true} size="xs" muted={true}>{label}</Text>;
    $[4] = label;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = is_expanded ? "arrow_drop_down" : "arrow_drop_up";
  let t7;
  if ($[6] !== t6) {
    t7 = <Icon muted={true} icon={t6} />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== is_expanded || $[9] !== t5 || $[10] !== t7) {
    t8 = <StyledCardSection as="button" type="button" aria-expanded={is_expanded} muted={true} p={t1} flex={t2} style={t3} onClick={t4}>{t5}{t7}</StyledCardSection>;
    $[8] = is_expanded;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== families || $[13] !== is_expanded) {
    t9 = is_expanded && families.map(_temp2);
    $[12] = families;
    $[13] = is_expanded;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <StyledCard overflow_hidden={true}>{t8}{t9}</StyledCard>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function _temp2(family) {
  return <StyledCardSection key={family} p={3} flex={{
    justify: "space-between",
    align: "center"
  }}><Text size="s">{familyLabel(family)}</Text><ScopeLevelSwitch family={family} /></StyledCardSection>;
}
function _temp(prev) {
  return !prev;
}
interface ApiKeyScopesFieldsProps {
  tier: ApiKeyTier;
  project_types: TeamType[] | undefined;
}
export function ApiKeyScopesFields(t0) {
  const $ = _c(10);
  const {
    tier,
    project_types
  } = t0;
  const {
    values
  } = useFormikContext();
  let t1;
  if ($[0] !== project_types || $[1] !== tier || $[2] !== values.scope_preset) {
    let t2;
    if ($[4] !== project_types) {
      t2 = t3 => {
        const {
          surface
        } = t3;
        return !surface || surfaceAppliesToProjects(surface, project_types);
      };
      $[4] = project_types;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== tier) {
      t3 = t4 => {
        const {
          label,
          surface: surface_0
        } = t4;
        return {
          label,
          families: groupFamilies(tier, surface_0)
        };
      };
      $[6] = tier;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const visible_scope_groups = scope_groups.filter(t2).map(t3).filter(_temp3);
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        direction: "column",
        gap: 3
      };
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Div flex={{
        direction: "column",
        gap: 0
      }}><Text semi={true} size="xs" muted={true} as="label">Scopes</Text><RadioCardsInput name="scope_preset" options={scope_preset_options} /></Div>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t1 = <Div flex={t4}>{t5}{values.scope_preset === "custom" && (visible_scope_groups.length === 0 ? <StyledCard p={3}><Text muted={true}>Select a project to see the scopes.</Text></StyledCard> : visible_scope_groups.map(_temp4))}</Div>;
    $[0] = project_types;
    $[1] = tier;
    $[2] = values.scope_preset;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp4(t0) {
  const {
    label: label_0,
    families: families_0
  } = t0;
  return <ScopeGroupCard key={label_0} label={label_0} families={families_0} />;
}
function _temp3(t0) {
  const {
    families
  } = t0;
  return families.length > 0;
}